import React, { useState, useEffect, lazy } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import ViewForm from './Form';
import { hasValue } from 'globalutils/fmUtils';
import { pageUrls } from 'globalconfig/constants/keys';
import { UI_DATE_FORMAT } from 'globalconfig/constants/static';
import Violations from '../Dashboard/Notfications';
import { mediaUrls } from 'globalconfig/constants/mediaUrls';
import SvgIcon from 'globalcomponents/SvgIcon/Base';

const layout = window?.$environment?.CURRENT_LAYOUT;
const Message = lazy(() => import(`globalcomponents/UI/Message/${layout}`));
const PdfView = lazy(() => import(`./OveridePdfViewer`));
const Link = lazy(() => import(`globalcomponents/UI/Link`));

// Utility function to format the daily document link
const getDailyDocumentLink = (driverId, day) =>
  `${pageUrls.dailyDocuments}?driver=${driverId}&day=${moment(day).format(UI_DATE_FORMAT)}`;

// Form Section Component
const FormSection = ({
  apiStatus,
  fetchLogBookView,
  history,
  callback,
  drivers,
  dates,
  logBook,
  currentDriver,
  fetchLogbookBreadcrumb,
  fetchLogbookBreadcrumbApiId,
  setFormValues,
  formValues,
  setNewDate,
  getDocumentToken,
  unSignedDates,
  i18n,
}) => (
  <div className="viewContainer">
    <ViewForm
      apiStatus={apiStatus}
      fetchLogBookView={fetchLogBookView}
      history={history}
      callback={callback}
      drivers={drivers}
      dates={dates}
      currentDate={logBook?.selectedDailyLog?.day || ''}
      currentDriver={currentDriver}
      fetchLogbookBreadcrumb={fetchLogbookBreadcrumb}
      fetchLogbookBreadcrumbApiId={fetchLogbookBreadcrumbApiId}
      setFormValues={setFormValues}
      formValues={formValues}
      changedDate={(e) => setNewDate(e)}
      getDocumentToken={getDocumentToken}
      unSignedDates={unSignedDates}
    />
    {
      <div className="linkContainer commonLinks">
        <Link
          className="uppercase dailyDocument"
          href={getDailyDocumentLink(logBook?.selectedDailyLog?.driverId, logBook?.selectedDailyLog?.day)}
        >
          <SvgIcon value={mediaUrls.viewDocument} />
          {i18n.t('logs.viewDocument')}
        </Link>
      </div>
    }
  </div>
);

// PDF Section Component
const PdfSection = ({
  logBook,
  newDate,
  currentDriver,
  fetchLogBookView,
  getDocumentToken,
  updateShowEvents,
  getLogbookEvents,
  regeneratePdf,
  callback,
  i18n,
  Styled,
  history,
}) => (
  <div className="leftWrapper">
    {logBook?.dailyLogs?.length ? (
      <PdfView
        list={logBook?.dailyLogs}
        i18n={i18n}
        date={newDate}
        driver={currentDriver.value}
        documentToken={logBook?.documentToken}
        Styled={Styled}
        updateShowEvents={updateShowEvents}
        getLogbookEvents={(idPub) =>
          getLogbookEvents({
            apiId: 'LoadMoreEvents',
            callback: callback,
            data: { id: idPub },
          })
        }
        fetchLogBookView={(date) =>
          fetchLogBookView({
            apiId: 'fetchLogBookView',
            callback: callback,
            data: { data: { date, driverId: currentDriver.value } },
          })
        }
        getDocumentToken={getDocumentToken}
        eventsData={logBook?.eventsData}
        selectedDailyLog={logBook?.selectedDailyLog}
        regeneratePdf={({ date, callbackFuntion }) =>
          regeneratePdf({
            apiId: 'fetchLogbookBreadcrumb',
            callback: callback,
            data: { data: { date: newDate, driverId: currentDriver.value } },
            newCallback: () => {
              fetchLogBookView({
                apiId: 'fetchLogBookView',
                callback: callback,
                data: { data: { driverId: currentDriver.value, date: newDate } },
                history,
              });
              callbackFuntion();
            },
          })
        }
      />
    ) : null}
  </div>
);

// Violations Section Component
const ViolationsSection = ({ violationsList }) => (
  <div className="rightSide">
    <Violations potentialViolations={violationsList} heading={'Violations'} />
  </div>
);

// Main Component
const Component = ({
  apiStatus,
  callback,
  fetchLogBookView,
  getLogbookEvents,
  updateShowEvents,
  i18n,
  history,
  drivers,
  dates,
  logBook,
  currentDriver,
  viewApiId,
  fetchLogbookBreadcrumb,
  fetchLogbookBreadcrumbApiId,
  Styled,
  getDocumentToken,
  unSignedDates,
  regeneratePdf,
}) => {
  const [formValues, setFormValues] = useState({
    driverId: logBook?.selectedDailyLog?.driverId,
    driverName: logBook?.selectedDailyLog?.driverName,
    type: 'DAILY_LOG',
  });

  const [newDate, setNewDate] = useState(null);

  useEffect(() => {
    setFormValues({
      driverId: logBook?.selectedDailyLog?.driverId,
      driverName: logBook?.selectedDailyLog?.driverName,
      type: 'DAILY_LOG',
    });
  }, [logBook?.selectedDailyLog?.driverId]);

  useEffect(() => {
    setNewDate(logBook?.selectedDailyLog?.day);
  }, [logBook]);

  const hasLogBookApiStatus = hasValue(apiStatus.status, 'api', viewApiId);

  return (
    <Styled.LogBookContainer includeRealtimeLog>
      {hasLogBookApiStatus?.errorMessage && <Message type="error" message={i18n.t(hasLogBookApiStatus.errorMessage)} />}
      <FormSection
        apiStatus={apiStatus}
        fetchLogBookView={fetchLogBookView}
        history={history}
        callback={callback}
        drivers={drivers}
        dates={dates}
        logBook={logBook}
        currentDriver={currentDriver}
        fetchLogbookBreadcrumb={fetchLogbookBreadcrumb}
        fetchLogbookBreadcrumbApiId={fetchLogbookBreadcrumbApiId}
        setFormValues={setFormValues}
        formValues={formValues}
        setNewDate={setNewDate}
        getDocumentToken={getDocumentToken}
        unSignedDates={unSignedDates}
        i18n={i18n}
      />
      <div className="displayContainer">
        <PdfSection
          logBook={logBook}
          newDate={newDate}
          currentDriver={currentDriver}
          fetchLogBookView={fetchLogBookView}
          getDocumentToken={getDocumentToken}
          updateShowEvents={updateShowEvents}
          getLogbookEvents={getLogbookEvents}
          regeneratePdf={regeneratePdf}
          callback={callback}
          i18n={i18n}
          Styled={Styled}
          history={history}
        />
        <ViolationsSection violationsList={logBook?.violationsListDto || []} />
      </div>
    </Styled.LogBookContainer>
  );
};

export default Component;

Component.propTypes = {
  apiStatus: PropTypes.object.isRequired,
  callback: PropTypes.func.isRequired,
  fetchLogBookView: PropTypes.func.isRequired,
  getLogbookEvents: PropTypes.func.isRequired,
  updateShowEvents: PropTypes.func.isRequired,
  i18n: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  drivers: PropTypes.object.isRequired,
  dates: PropTypes.array.isRequired,
  logBook: PropTypes.object.isRequired,
  currentDriver: PropTypes.object.isRequired,
  viewApiId: PropTypes.string.isRequired,
  fetchLogbookBreadcrumb: PropTypes.func.isRequired,
  fetchLogbookBreadcrumbApiId: PropTypes.string.isRequired,
  Styled: PropTypes.object.isRequired,
  getDocumentToken: PropTypes.func.isRequired,
  unSignedDates: PropTypes.array.isRequired,
  regeneratePdf: PropTypes.func.isRequired,
};
